import React, { useState } from 'react';
import { CssBaseline, Container, Paper, Typography, Box, Grid } from '@mui/material';
import { useAuth } from 'react-oidc-context';
import { ThemeContextProvider, useTheme } from './contexts/ThemeContext';
import ICCareerGoalsSection from './components/ICCareerGoalsSection';
import CareerProfileSection from './components/CareerProfileSection';
import EarlyCareerTalentSection from './components/EarlyCareerTalentSection';
import DirectReportsFilter from './components/DirectReportsFilter';
import ThemeToggle from './components/ThemeToggle';
import DebugViewToggle from './components/DebugViewToggle';
import { DirectReport } from './types';

const AppContent: React.FC = () => {
  const auth = useAuth();
  const { mode } = useTheme();
  const [selectedDirectReport, setSelectedDirectReport] = useState<DirectReport | null>(null);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [managerStatusLoaded, setManagerStatusLoaded] = useState<boolean>(false);
  const [userAlias, setUserAlias] = useState<string>('user');
  // Debug-only: lets allowlisted users flip between IC and manager views (UI only)
  const [debugViewOverride, setDebugViewOverride] = useState<boolean>(false);

  React.useEffect(() => {
    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      console.log('User not authenticated, redirecting to login...');
      auth.signinRedirect();
      return;
    }

    if (auth.user) {
      const username =
        auth.user.profile.preferred_username || auth.user.profile['cognito:alias'] || auth.user.profile.sub;
      console.log('User authenticated:', username);
      setUserAlias(username as string);
      (window as any).__currentUserAlias = username;
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) {
      return;
    }

    const validateManagerStatus = async () => {
      try {
        const response = await fetch('/api/v1/user/manager-status', {
          headers: {
            Authorization: `Bearer ${auth.user?.id_token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setIsManager(data.is_manager);
          if (data.alias) {
            setUserAlias(data.alias);
            (window as any).__currentUserAlias = data.alias;
          }
        } else {
          console.error('Failed to validate manager status:', response.status);
          setIsManager(false);
        }
      } catch (error) {
        console.error('Error validating manager status:', error);
        setIsManager(false);
      } finally {
        setManagerStatusLoaded(true);
      }
    };

    validateManagerStatus();
  }, [auth.isAuthenticated, auth.user]);

  if (auth.isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, textAlign: 'center' }}>
        <Typography>Authenticating...</Typography>
      </Container>
    );
  }

  if (!managerStatusLoaded) {
    return (
      <Container maxWidth="xl" sx={{ py: 3, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  const showManagerView = debugViewOverride ? !isManager : isManager;

  return (
    <>
      <CssBaseline />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            background:
              mode === 'dark'
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={3}>
            <Box>
              <Typography variant="h1" component="h1" gutterBottom>
                SA Talent Card
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Welcome, {userAlias} {showManagerView ? '(Manager)' : '(IC)'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <DebugViewToggle
                userAlias={userAlias}
                showManagerView={showManagerView}
                onToggle={() => setDebugViewOverride((prev) => !prev)}
              />
              <ThemeToggle />
            </Box>
          </Box>
        </Paper>

        {showManagerView && (
          <DirectReportsFilter selectedReport={selectedDirectReport} onReportChange={setSelectedDirectReport} />
        )}

        <Grid container spacing={3}>
          {showManagerView ? (
            <>
              <Grid item xs={12}>
                <CareerProfileSection selectedAlias={selectedDirectReport?.alias} />
              </Grid>
              <Grid item xs={12}>
                <EarlyCareerTalentSection selectedAlias={selectedDirectReport?.alias} />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <ICCareerGoalsSection />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

function App() {
  return (
    <ThemeContextProvider>
      <AppContent />
    </ThemeContextProvider>
  );
}

export default App;
